/* ============================================================
   PintaFlow — Design Direction HYBRID: "Atelier OS"
   ----------------------------------------------------------
   V1 (Electric Operator) + V2 (Studio Editorial)

   Keep from V1:
     - Asymmetric layout (left rail tagline, right pipeline)
     - Wired pipeline diagram with telemetry
     - Creator OS positioning, mono eyebrows, system labels
     - Information architecture (manifesto + capability grid)
     - Electric Blue accent (#3B82F6) — operating-system signal

   Add from V2:
     - Fraunces serif display (with italic emphasis)
     - Warm black base (#0B0A09) + ivory text (#F2EDE3)
     - Editorial voice: "Vol. 01", numbered capabilities
     - Premium magazine-grade typography + spacing rhythm
   ============================================================ */
import { ArrowUpRight, Terminal, ChevronRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STAGES = [
  { id: 1, label: "Idea", note: "Capture every spark." },
  { id: 2, label: "Research", note: "Gather the context." },
  { id: 3, label: "Script", note: "Write the hook." },
  { id: 4, label: "Create", note: "Record. Edit. Ship." },
  { id: 5, label: "Schedule", note: "Pick the moment." },
  { id: 6, label: "Published", note: "Out in the wild." },
];

const SAMPLE_CARDS = [
  { title: "Why I quit FAANG (3-min hook test)", platform: "YT LONG", stage: "Script", dot: "#F59E0B" },
  { title: "Editing for retention — under 60s", platform: "REELS", stage: "Create", dot: "#3B82F6" },
  { title: "Tools I used to grow to 100k", platform: "TWEET", stage: "Published", dot: "#10B981" },
  { title: "How to research a niche fast", platform: "BLOG", stage: "Research", dot: "#3B82F6" },
];

const CAPABILITIES = [
  ["Idea Vault", "Tags, filters, search. Nothing leaks."],
  ["Swipe File", "URLs auto-fetch their cover. Inspiration, indexed."],
  ["Streak", "A monochrome heatmap. No gamification confetti."],
  ["Goals", "Weekly + monthly. Auto-progress on publish."],
  ["Calendar", "Your publishing month, on one page."],
  ["Offline PWA", "Capture without signal. Sync when you land."],
];

export default function LandingHybrid() {
  return (
    <div
      className="min-h-screen bg-[#0B0A09] text-[#F2EDE3] overflow-x-hidden relative"
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />

      {/* Subtle blueprint grid — V1 signal */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #3B82F6 1px, transparent 1px), linear-gradient(to bottom, #3B82F6 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 80% 60% at 70% 35%, black 30%, transparent 100%)",
        }}
      />

      {/* Top bar */}
      <header className="relative border-b border-[#F2EDE3]/[0.08] backdrop-blur-sm z-10">
        <div className="max-w-[1320px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-7 h-7 rounded-md bg-[#3B82F6] flex items-center justify-center shadow-[0_0_24px_rgba(59,130,246,0.5)]">
              <div className="w-2.5 h-2.5 bg-[#F2EDE3] rounded-sm" />
            </div>
            <span
              className="text-xl font-medium tracking-tight"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'SOFT' 50" }}
            >
              PintaFlow
            </span>
            <span
              className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-[#F2EDE3]/45 ml-3"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              / Creator OS
            </span>
          </div>
          <div className="flex items-center gap-5">
            <span
              className="hidden md:inline text-[10px] uppercase tracking-[0.22em] text-[#F2EDE3]/35"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              vol. 01 — private beta
            </span>
            <button
              onClick={startGoogleLogin}
              className="text-sm text-[#F2EDE3]/80 hover:text-[#F2EDE3] inline-flex items-center gap-1.5 transition"
            >
              Sign in <ArrowUpRight size={13} />
            </button>
          </div>
        </div>
      </header>

      {/* HERO — V1 asymmetric layout with V2 typography */}
      <section className="relative max-w-[1320px] mx-auto px-6 pt-16 md:pt-24 pb-20 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-14 items-start">
          {/* LEFT RAIL */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#3B82F6]/35 bg-[#3B82F6]/[0.08] text-[#9DBEFF] text-[10px] uppercase tracking-[0.24em] mb-8"
                 style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
              now booting · vol. 01
            </div>

            <h1
              className="text-[44px] sm:text-[60px] lg:text-[72px] leading-[0.95] tracking-[-0.025em] text-[#F2EDE3]"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 96, 'SOFT' 30, 'wght' 500" }}
            >
              The <em className="not-italic text-[#3B82F6]">operating</em><br />
              <span className="italic" style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 60, 'wght' 500" }}>
                system
              </span> for<br />
              serious creators.
            </h1>

            <p className="mt-7 text-[#F2EDE3]/65 text-[15px] leading-[1.65] max-w-md">
              PintaFlow runs your craft like a studio. Capture, research, write, ship — every idea routed through one
              pipeline that respects your focus and your taste.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={startGoogleLogin}
                className="group inline-flex items-center gap-2 bg-[#F2EDE3] text-[#0B0A09] px-5 py-3 rounded-md font-medium text-sm hover:bg-[#3B82F6] hover:text-[#F2EDE3] transition-colors"
              >
                Boot the OS
                <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
              </button>
              <span
                className="text-[10px] uppercase tracking-[0.24em] text-[#F2EDE3]/40"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                G · sign in with google
              </span>
            </div>

            <div
              className="mt-12 flex items-center gap-5 text-[10px] uppercase tracking-[0.2em] text-[#F2EDE3]/30"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <span>5 platforms</span>
              <span className="text-[#3B82F6]/60">·</span>
              <span>6 stages</span>
              <span className="text-[#3B82F6]/60">·</span>
              <span>1 brain</span>
            </div>
          </div>

          {/* RIGHT — PIPELINE blueprint (V1 hero, dressed in V2 type) */}
          <div className="lg:col-span-7 relative">
            <div
              className="absolute -top-3 left-0 right-0 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[#F2EDE3]/30"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <span className="flex items-center gap-1.5">
                <Terminal size={10} /> pipeline.flow
              </span>
              <span>idea → published</span>
            </div>

            <div className="border border-[#F2EDE3]/[0.09] rounded-2xl bg-gradient-to-br from-[#F2EDE3]/[0.025] via-transparent to-[#3B82F6]/[0.03] p-5 md:p-7 backdrop-blur-sm">
              {/* Stage chips */}
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                {STAGES.map((s, i) => (
                  <div key={s.id} className="relative">
                    <div className="rounded-lg border border-[#F2EDE3]/[0.08] bg-[#0B0A09]/60 px-3 py-3 hover:border-[#3B82F6]/55 hover:bg-[#3B82F6]/[0.07] transition group">
                      <div
                        className="text-[9px] text-[#3B82F6] tracking-[0.22em]"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        0{s.id}
                      </div>
                      <div
                        className="text-[14px] font-medium mt-1.5 text-[#F2EDE3] tracking-tight"
                        style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32" }}
                      >
                        {s.label}
                      </div>
                      <div className="text-[10px] text-[#F2EDE3]/40 mt-1 leading-snug">{s.note}</div>
                    </div>
                    {i < STAGES.length - 1 && (
                      <div className="hidden lg:flex absolute top-1/2 -right-2 w-3 h-px bg-gradient-to-r from-[#3B82F6] to-transparent" />
                    )}
                  </div>
                ))}
              </div>

              {/* Sample cards (mock movement) */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {SAMPLE_CARDS.map((c, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-[#F2EDE3]/[0.08] bg-[#0B0A09]/70 p-3.5 hover:border-[#F2EDE3]/20 transition"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-[9px] px-1.5 py-0.5 bg-[#F2EDE3]/[0.06] rounded text-[#F2EDE3]/60 tracking-[0.18em] uppercase"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        {c.platform}
                      </span>
                      <span
                        className="text-[9px] tracking-[0.18em] uppercase flex items-center gap-1"
                        style={{ color: c.dot, fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
                        {c.stage}
                      </span>
                    </div>
                    <div
                      className="text-[14px] text-[#F2EDE3]/90 leading-snug tracking-tight"
                      style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24" }}
                    >
                      {c.title}
                    </div>
                  </div>
                ))}
              </div>

              {/* Telemetry strip */}
              <div className="mt-8 pt-5 border-t border-[#F2EDE3]/[0.07] grid grid-cols-3 gap-3 text-center">
                {[
                  ["12", "ideas captured", "#F2EDE3"],
                  ["07", "in pipeline", "#F2EDE3"],
                  ["04", "published / week", "#3B82F6"],
                ].map(([n, l, color], i) => (
                  <div key={i}>
                    <div
                      className="text-[34px] leading-none"
                      style={{
                        color,
                        fontFamily: "'Fraunces', serif",
                        fontVariationSettings: "'opsz' 96, 'SOFT' 30, 'wght' 500",
                      }}
                    >
                      {n}
                    </div>
                    <div
                      className="text-[9px] uppercase tracking-[0.22em] text-[#F2EDE3]/35 mt-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO — V1 strip, V2 editorial voice */}
      <section className="relative border-y border-[#F2EDE3]/[0.08] py-16 md:py-20 bg-[#08070691] backdrop-blur-sm z-10">
        <div className="max-w-[1320px] mx-auto px-6 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <div
              className="text-[10px] uppercase tracking-[0.3em] text-[#3B82F6] mb-3"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              // ch.01 — what it is
            </div>
            <div
              className="text-[#F2EDE3]/40 text-sm italic"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              A short manifesto.
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2
              className="text-[28px] sm:text-[40px] lg:text-[48px] leading-[1.05] tracking-[-0.02em]"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 500" }}
            >
              Not a notes app. <em className="not-italic text-[#3B82F6]">Not</em> an AI generator.{" "}
              <span className="text-[#F2EDE3]/45">
                PintaFlow is the <em className="italic" style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 70" }}>operating layer</em>
                {" "}between your ideas and the audience that&apos;s already waiting for them.
              </span>
            </h2>
          </div>
        </div>
      </section>

      {/* CAPABILITIES — V1 grid + V2 numbered editorial */}
      <section className="relative max-w-[1320px] mx-auto px-6 py-20 md:py-24 z-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.3em] text-[#3B82F6] mb-3"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              // ch.02 — the system
            </div>
            <h3
              className="text-3xl md:text-5xl tracking-[-0.02em]"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 500" }}
            >
              Six instruments. <em className="italic text-[#F2EDE3]/55">One</em> studio.
            </h3>
          </div>
          <div className="text-sm text-[#F2EDE3]/45 max-w-xs">
            Each module is opinionated, fast, and refuses to compete for your attention.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CAPABILITIES.map(([t, d], i) => (
            <div
              key={t}
              className="group border border-[#F2EDE3]/[0.08] rounded-lg p-6 hover:border-[#3B82F6]/40 hover:bg-[#3B82F6]/[0.035] transition relative"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="text-[10px] text-[#3B82F6] tracking-[0.24em]"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  SYS.{String(i + 1).padStart(2, "0")}
                </div>
                <ChevronRight size={14} className="text-[#F2EDE3]/20 group-hover:text-[#3B82F6] group-hover:translate-x-1 transition" />
              </div>
              <div
                className="text-xl font-medium tracking-tight text-[#F2EDE3]"
                style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32" }}
              >
                {t}
              </div>
              <div className="text-sm text-[#F2EDE3]/55 mt-2 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA strip */}
      <section className="relative border-t border-[#F2EDE3]/[0.08] py-20 md:py-28 z-10">
        <div className="max-w-[1320px] mx-auto px-6 text-center">
          <div
            className="text-[10px] uppercase tracking-[0.3em] text-[#3B82F6] mb-5"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            // ch.03 — the invitation
          </div>
          <h4
            className="text-3xl md:text-5xl lg:text-6xl tracking-[-0.025em] max-w-3xl mx-auto leading-[1.02]"
            style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 144, 'SOFT' 50, 'wght' 500" }}
          >
            Stop juggling tabs. <em className="italic text-[#3B82F6]">Start</em> shipping.
          </h4>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={startGoogleLogin}
              className="group inline-flex items-center gap-2 bg-[#3B82F6] text-[#F2EDE3] px-6 py-3.5 rounded-md font-medium text-sm hover:bg-[#F2EDE3] hover:text-[#0B0A09] transition-colors shadow-[0_0_40px_rgba(59,130,246,0.35)]"
            >
              Boot the OS
              <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
            </button>
            <span
              className="text-[10px] uppercase tracking-[0.24em] text-[#F2EDE3]/35"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              free in private beta · no card
            </span>
          </div>
        </div>
      </section>

      <footer className="relative border-t border-[#F2EDE3]/[0.08] py-8 z-10">
        <div className="max-w-[1320px] mx-auto px-6 flex flex-wrap justify-between text-[10px] uppercase tracking-[0.22em] text-[#F2EDE3]/30"
             style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <span>© 2026 pintaflow</span>
          <span>direction · atelier os · hybrid</span>
        </div>
      </footer>
    </div>
  );
}
